import classNames from 'classnames';
import {ModalOverlay} from 'modules/modals/components/ModalOverlay';
import React, {FC, MouseEventHandler, ReactNode, useContext, useMemo} from 'react';
import {PortalContext} from 'modules/portal/context/PortalContext';
import {ShadowBlock} from 'components/ShadowBlock';
import Icon from 'components/Icon';
import {ButtonV3} from 'components/ButtonV3';
import style from './style.module.scss';

export interface IProps {
    hasOverlay?: boolean;
    hasClose?: boolean;
    adaptive?: boolean;
    animated?: boolean;
    className?: string;
    classNameCloseButton?: string;
    classNameCloseIcon?: string;
    classNameTitle?: string;
    classNameContent?: string;
    overlayClassName?: string;
    children: ReactNode;
    onClose?: MouseEventHandler<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>;
    overlay?: ReactNode;
    isMobileBottomAlign?: boolean;
    title?: ReactNode;
    bottomPanel?: ReactNode;
    type?: 'modal' | 'dialog';
}

type TModalType = 'modal' | 'dialog';

const getVisibilityClass = (
    type: TModalType,
    visibility: boolean,
    animated: boolean | undefined,
): Record<string, boolean> => {
    const state = animated ? 'Animated' : '';
    const visibilityState = visibility ? 'IsVisible' : 'IsHidden';
    const className = `${type}${visibilityState}${state}`;

    return {[style[className]]: true};
};

const CloseButton: FC<{
    className?: string;
    classNameIcon?: string;
    onClose?: MouseEventHandler<HTMLButtonElement>;
}> = ({className, classNameIcon, onClose}) => (
    <ButtonV3
        className={classNames(style.close, className, 'regular-modal-close-button')}
        data-modal-action={'close'}
        onClick={onClose}
        isIconButton={true}
        theme={'secondary'}
        variants={{MB: 'small', TB: 'medium', DT: 'medium'}}
    >
        <Icon className={classNameIcon} icon={'close'} />
    </ButtonV3>
);

export const RegularModal: FC<IProps> = ({
    hasOverlay = true,
    children,
    animated,
    adaptive,
    hasClose,
    className,
    classNameCloseButton,
    classNameCloseIcon,
    classNameTitle,
    classNameContent,
    overlayClassName,
    overlay = <ModalOverlay data-modal-action={'close'} />,
    onClose,
    isMobileBottomAlign = false,
    title,
    bottomPanel,
    type = 'modal',
}) => {
    const {visibility} = useContext(PortalContext);
    const isModal = 'modal' === type;

    const visibilityClasses = useMemo(
        () => getVisibilityClass(type, visibility, animated),
        [type, visibility, animated],
    );

    const closeButton = useMemo(
        () =>
            hasClose ? (
                <CloseButton className={classNameCloseButton} classNameIcon={classNameCloseIcon} onClose={onClose} />
            ) : null,
        [hasClose, classNameCloseButton, classNameCloseIcon, onClose],
    );

    const containerClassName = classNames(isModal ? style.modal : style.dialog, visibilityClasses, overlayClassName, {
        [style.fullWidth]: isModal && isMobileBottomAlign,
    });

    const contentClassName = classNames('regular-modal-content', style[`${type}Content`], className, classNameContent, {
        [style[`${type}Adaptive`]]: adaptive,
        [style.mobileBottom]: isModal && isMobileBottomAlign,
        [style.contentWithTitle]: Boolean(title),
    });

    const titleClassName = classNames(style[`${type}Title`]);
    const bottomClassName = classNames(style[`${type}Bottom`]);

    return (
        <div className={containerClassName}>
            <div className={contentClassName}>
                {isModal && !title && closeButton}

                {title && (
                    <div className={titleClassName}>
                        {isModal && closeButton}
                        <div className={classNameTitle}>{title}</div>
                    </div>
                )}

                {children}

                {bottomPanel && <div className={bottomClassName}>{bottomPanel}</div>}
            </div>

            <ShadowBlock onClick={onClose}>{hasOverlay && overlay}</ShadowBlock>
        </div>
    );
};
